import React, { useState, useEffect, useMemo, useContext } from 'react';
import { UserContext } from '../../Context/UserContext';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { checkIsAdmin, checkIsGlobalAdmin } from '../../utils/authUtils';
import { FaArrowLeft, FaCheese, FaTrash, FaCheckCircle, FaExclamationTriangle, FaSearch, FaEdit, FaTimes, FaPlus, FaUsers, FaMapMarkerAlt, FaCogs } from 'react-icons/fa';
import CustomerCustomizationModal from './CustomerCustomizationModal';
import './CreateCustomerPage.css';

export default function Toppings() {
  const { baseUrl, configLoading, getHeaders } = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();
  const url = baseUrl || "";
  
  const [formData, setFormData] = useState({ topping_name: '', price: '' });
  const [toppings, setToppings] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [toppingToDelete, setToppingToDelete] = useState(null);
  
  // Customization
  const [showCustomizeModal, setShowCustomizeModal] = useState(false);

  // Edit State
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [toppingToEdit, setToppingToEdit] = useState(null);
  const [editFormData, setEditFormData] = useState({ topping_name: '', price: '' });
  const [editBranches, setEditBranches] = useState([]);
  const [editCompanies, setEditCompanies] = useState([]);
  
  // Multi-Company State
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [companyBranchesMap, setCompanyBranchesMap] = useState({});

  // Branch and Role State
  const [availableBranches, setAvailableBranches] = useState([]);
  const [selectedBranchFilter, setSelectedBranchFilter] = useState(localStorage.getItem('active_branch') || 'All Branches');
  const [selectedCompanyFilter, setSelectedCompanyFilter] = useState(localStorage.getItem('active_company') || 'All Companies');
  const [selectedAddBranches, setSelectedAddBranches] = useState([]);
  const [isCompanyAdmin, setIsCompanyAdmin] = useState(false);
  const [isGroupAdmin, setIsGroupAdmin] = useState(false);
  const [isBranchAdmin, setIsBranchAdmin] = useState(false);
  const [userBranch, setUserBranch] = useState('');
  const [companyName, setCompanyName] = useState('');

  // Permission State
  const [canWrite, setCanWrite] = useState(true); // Default to true if permissions API missing
  const [canDelete, setCanDelete] = useState(true);
  const [customFieldsData, setCustomFieldsData] = useState({});
  const [editCustomFieldsData, setEditCustomFieldsData] = useState({});

  // DocType State
  const [doctypeData, setDoctypeData] = useState(null);

  // Detect role and fetch branches
  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const user = JSON.parse(userStr);
      const activeBranch = localStorage.getItem('active_branch');
      const branch = (activeBranch && activeBranch !== 'All Branches' && activeBranch !== 'All') ? activeBranch : (user.branch_name || user.branch || "");
      const company = user.company_name || user.company || "";
      setUserBranch(branch);
      setCompanyName(company);
      
      const roleLower = user.role?.toLowerCase() || '';
      const isGroupAdminRole = checkIsGlobalAdmin(user);
      const isAdminRole = checkIsAdmin(user);
      const isBA = roleLower.includes('branch') || roleLower.includes('manager');
      
      setIsCompanyAdmin(isAdminRole);
      setIsGroupAdmin(isGroupAdminRole);
      setIsBranchAdmin(isBA);
      
      fetchCompanies();
    } else {
      fetchCompanies(); // fallback
    }
  }, []);

  const fetchCompanies = async () => {
    try {
      const userStr = localStorage.getItem('user');
      const userObj = userStr ? JSON.parse(userStr) : {};
      
      let comps = [];
      if (userObj.companies && userObj.companies.length > 0) {
        comps = userObj.companies.map(c => typeof c === 'object' && c !== null ? (c.company_name || c.company || c.name || '') : c).filter(c => c && typeof c === 'string');
      } else if (userObj.company_name || userObj.company) {
        comps = [userObj.company_name || userObj.company];
      } else {
        try {
          const res = await axios.get(`${url}/api/company-details`, { headers: getHeaders() });
          const details = res.data?.companyDetails || (Array.isArray(res.data) ? res.data : []);
          comps = details.map(d => d.restaurantName || d.company_name).filter(n => n);
        } catch (e) { console.error(e); }
      }
      
      // Fetch public companies if still empty
      if (comps.length === 0) {
        try {
           const res = await axios.get(`${url}/api/companies-public`);
           const details = res.data || [];
           comps = details.map(d => d.company_name).filter(n => n);
        } catch(e) {}
      }

      const finalComps = comps.length > 0 ? [...new Set(comps)] : ['POS 8'];
      setCompanyOptions(finalComps);

      const activeContext = localStorage.getItem('active_company');
      if (selectedCompanies.length === 0 && finalComps.length > 0) {
        if (activeContext && activeContext !== 'All' && finalComps.includes(activeContext)) {
          setSelectedCompanies([activeContext]);
        } else if (activeContext === 'All') {
          setSelectedCompanies([]);
        } else if (!activeContext && finalComps.length === 1) {
          setSelectedCompanies([finalComps[0]]);
        }
      }

      if (finalComps.length > 0) {
        const branchPromises = finalComps.map(comp => 
          axios.get(`${url}/api/branches?company_name=${encodeURIComponent(comp)}`, { headers: getHeaders(null, comp) })
            .then(res => ({ comp, data: res.data }))
        );
        const results = await Promise.all(branchPromises);
        const newMap = {};
        const allBranches = [];
        results.forEach(res => {
           const branches = res.data.map(b => typeof b === 'string' ? b : b.branch_name || b.branch || b.name || '').filter(b => b);
           newMap[res.comp] = branches;
           allBranches.push(...branches);
        });
        setCompanyBranchesMap(newMap);
        setAvailableBranches([...new Set(allBranches)]);
      }
    } catch (e) { console.error(e); }
  };

  // Fetch Permissions and Doctype
  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const user = JSON.parse(userStr);
          const role = user.role;
          if (role) {
            const apiurl = url ? `${url}/api/role-permissions?role=${role}` : `/api/role-permissions?role=${role}`;
            const response = await axios.get(apiurl);
            const perms = response.data.permissions || [];
            // Match pageId with toppings page
            const pagePerm = perms.find(p => p.pageId === 'toppings'); 
            if (pagePerm) {
              setCanWrite(pagePerm.canWrite === true);
              setCanDelete(pagePerm.canDelete === true);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching permissions:", error);
      }
    };
    fetchPermissions();
    fetchDoctype();
  }, [url]);

  const fetchDoctype = async () => {
    try {
      const response = await axios.get(`${url}/api/doctypes/Toppings`, { headers: getHeaders() });
      setDoctypeData(response.data);
    } catch (err) {
      console.error("Error fetching Toppings doctype:", err);
    }
  };

  useEffect(() => {
    const handleStorage = () => {
      setSelectedBranchFilter(localStorage.getItem('active_branch') || 'All Branches');
      setSelectedCompanyFilter(localStorage.getItem('active_company') || 'All Companies');
    };
    window.addEventListener('local-storage-change', handleStorage);
    return () => window.removeEventListener('local-storage-change', handleStorage);
  }, []);

  useEffect(() => {
    fetchToppings();
  }, [selectedBranchFilter, selectedCompanyFilter, getHeaders]);

  const fetchToppings = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get(`${url}/api/toppings`, { headers: getHeaders() });
      setToppings(res.data || []);
    } catch (err) {
      console.error('Error fetching toppings:', err);
      setError('Failed to fetch toppings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filteredToppings = useMemo(() => {
    if (!toppings) return [];
    
    const searched = toppings.filter(topping =>
      (topping.topping_name?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (topping.name?.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    // Filtering logic similar to AddItemGroupPage
    return searched.filter(t => {
      const compRaw = Array.isArray(t.company_names) && t.company_names.length > 0 ? t.company_names[0] : 
                      (Array.isArray(t.company_name) && t.company_name.length > 0 ? t.company_name[0] : t.company_name || "");
      const comp = compRaw.toString().toLowerCase().trim();
      
      const tBranchRaw = t.branch_names || t.branch_name || "Global Access";
      const tBranches = Array.isArray(tBranchRaw) ? tBranchRaw : tBranchRaw.split(',').map(b => b.trim());

      const activeComp = (selectedCompanyFilter || "All Companies").toLowerCase().trim();
      const compMatch = activeComp === 'all companies' || activeComp === 'all' || comp === 'all' || comp === 'global' || comp === '' || comp === activeComp;
      if (!compMatch) return false;

      const activeBranch = (selectedBranchFilter || "All Branches").toLowerCase().trim();
      const branchMatch = activeBranch === 'all branches' || activeBranch === 'all' || tBranches.some(b => {
        const bl = b.toLowerCase().trim();
        return bl === activeBranch;
      });
      if (!branchMatch) return false;

      return true;
    });
  }, [toppings, searchTerm, selectedCompanyFilter, selectedBranchFilter]);

  const handleGoBack = () => navigate(-1);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCustomFieldChange = (id, value, isEdit = false) => {
    if (isEdit) {
      setEditCustomFieldsData(prev => ({ ...prev, [id]: value }));
    } else {
      setCustomFieldsData(prev => ({ ...prev, [id]: value }));
    }
  };

  const submitToppingData = async () => {
    const activeContext = localStorage.getItem('active_company');
    const validBranches = selectedAddBranches.filter(b => b && b.trim() !== "");
    const companiesToProcess = selectedCompanies.length > 0 ? selectedCompanies : (activeContext !== 'All' ? [activeContext] : [companyName]);
    const branchesToProcess = validBranches.length > 0 ? validBranches : [""];

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Include standard fields and custom fields
      const payload = {
        topping_name: formData.topping_name || customFieldsData.topping_name || customFieldsData.name || `Topping-${Date.now()}`,
        price: parseFloat(formData.price || customFieldsData.price || 0),
        company_name: companiesToProcess.length > 0 ? companiesToProcess : undefined,
        branch_name: branchesToProcess.filter(b => b).length > 0 ? branchesToProcess.filter(b => b) : undefined,
        ...customFieldsData
      };

      const headers = getHeaders();
      if (companiesToProcess.length > 0) headers['X-Company-Name'] = companiesToProcess[0];

      await axios.post(`${url}/api/toppings`, payload, { headers });
      setSuccess(`Successfully added topping.`);
      setFormData({ topping_name: '', price: '' });
      setCustomFieldsData({});
      // Keep selected branches/companies so user doesn't have to re-select
      fetchToppings();

      // If we came from another page (like CreateItemsPage), go back automatically
      if (location.state && location.state.returnPath) {
        setTimeout(() => {
          navigate(location.state.returnPath, {
            state: { ...location.state }
          });
        }, 1500);
      }
    } catch (err) {
      setError(`Failed to save topping: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
      setShowAssignmentModal(false);
    }
  };

  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    if (!canWrite) {
      setError("You do not have permission to add toppings.");
      return;
    }

    if (!formData.topping_name && !customFieldsData.topping_name) {
      setError("Topping Name is required.");
      return;
    }

    // ALWAYS open the popup first to confirm Company and Branch!
    setShowAssignmentModal(true);
  };

  const handleConfirmAndSaveAssignments = async () => {
    // If the form is filled out, this confirms the options AND saves it!
    if (formData.topping_name || customFieldsData.topping_name) {
      await submitToppingData();
    } else {
      // If the form isn't filled out, they just clicked to set defaults, so just close.
      setShowAssignmentModal(false);
    }
  };

  const openDeleteModal = (id) => {
    if (!canDelete) {
      setError("You do not have permission to delete toppings.");
      return;
    }
    setToppingToDelete(id);
    setShowDeleteModal(true);
  };

  const handleDelete = async (id) => {
    if (!id) {
        setError("Invalid topping ID for deletion.");
        return;
    }
    try {
      setLoading(true);
      setError(null);
      await axios.delete(`${url}/api/toppings/${id}`, { headers: getHeaders() });
      setSuccess('Topping deleted successfully');
      fetchToppings();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete topping');
    } finally {
      setLoading(false);
      setShowDeleteModal(false);
      setToppingToDelete(null);
    }
  };

  const openEditModal = (topping) => {
    setToppingToEdit(topping);
    setEditFormData({
      topping_name: topping.topping_name || topping.name || '',
      price: topping.price || 0
    });
    
    const b = topping.branch_names || topping.branch_name || [];
    const existingBranches = Array.isArray(b) ? [...b] : (b ? b.split(',').map(s => s.trim()) : []);
    setEditBranches(existingBranches);
    
    const comps = topping.company_names || topping.company_name || [];
    const initialComps = Array.isArray(comps) ? [...comps] : (comps ? comps.split(',').map(s => s.trim()) : []);
    setEditCompanies(initialComps);
    
    // Load custom fields into edit state
    const customFields = {};
    if (doctypeData?.fields) {
      doctypeData.fields.forEach(f => {
        let val = topping[f.id] || "";
        customFields[f.id] = val;
      });
    }
    setEditCustomFieldsData(customFields);
    
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setToppingToEdit(null);
    setEditBranches([]);
    setEditCompanies([]);
    setEditCustomFieldsData({});
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    const validEditBranches = editBranches.filter(b => b && b.trim() !== "");

    try {
      setLoading(true);
      setError(null);
      
      const comp = toppingToEdit.company_name || "";
      const payload = { 
        topping_name: editFormData.topping_name || editCustomFieldsData.topping_name || editCustomFieldsData.name,
        price: parseFloat(editFormData.price || editCustomFieldsData.price || 0),
        branch_name: validEditBranches.length > 0 ? validEditBranches : undefined,
        company_name: Array.isArray(comp) ? comp : [comp],
        ...editCustomFieldsData
      };

      await axios.put(`${url}/api/toppings/${toppingToEdit._id}`, payload, { headers: getHeaders() });
      setSuccess(`Topping updated successfully`);
      setShowEditModal(false);
      fetchToppings();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update topping');
    } finally {
      setLoading(false);
    }
  };

  if (configLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: 'linear-gradient(135deg, #ffffff 0%, #3498db 100%)' }}>
        <div style={{ textAlign: 'center', color: '#3498db', fontSize: '18px' }}>
          <FaCheese style={{ fontSize: '48px', marginBottom: '20px', color: '#3498db' }} />
          <p>Loading toppings...</p>
        </div>
      </div>
    );
  }

  // Same visibility checks as AddItemGroupPage
  const activeCompanyLocal = (localStorage.getItem('active_company') || 'All').trim();
  const isSpecificCompanyActive = activeCompanyLocal.toLowerCase() !== 'all companies' && activeCompanyLocal.toLowerCase() !== 'all' && activeCompanyLocal.toLowerCase() !== 'global' && activeCompanyLocal !== '';
  const activeBranchLocal = (localStorage.getItem('active_branch') || 'All Branches').trim();
  const isSpecificBranchActive = activeBranchLocal.toLowerCase() !== 'all branches' && activeBranchLocal.toLowerCase() !== 'all' && activeBranchLocal !== '';
  
  // Force visible or dynamic like AddItemGroupPage
  const showCompanyAssign = (isGroupAdmin || isCompanyAdmin) && companyOptions.length > 1 && !isSpecificCompanyActive && !isSpecificBranchActive;
  const showBranchAssign = (isGroupAdmin || isCompanyAdmin) && !isSpecificBranchActive && availableBranches.length > 0;

  const editAvailableBranches = toppingToEdit ? (companyBranchesMap[Array.isArray(toppingToEdit.company_name) ? toppingToEdit.company_name[0] : (toppingToEdit.company_name || toppingToEdit.company)] || availableBranches || []) : availableBranches;
  const uniqueEditAvailableBranches = [...new Set(editAvailableBranches)];
  
  const showEditCompanyAssign = (isGroupAdmin || isCompanyAdmin) && companyOptions.length > 1 && !isSpecificCompanyActive && !isSpecificBranchActive;
  const showEditBranchAssign = (isGroupAdmin || isCompanyAdmin) && !isSpecificBranchActive && editCompanies.length > 0 && uniqueEditAvailableBranches.length > 0;

  return (
    <div className="create-customer-container main-customer-page">
      <div className="customer-header-section">
        <div className="header-left">
          <button onClick={handleGoBack} className="header-back-btn" disabled={loading}>
            <FaArrowLeft /> Back
          </button>
        </div>

        <div className="header-center">
          <h1>Toppings</h1>
        </div>

        <div className="header-right">
          <div className="header-actions">
            
            {/* The Company Button */}
            {showCompanyAssign && (
              <button 
                type="button"
                className="customize-btn" 
                onClick={() => setShowAssignmentModal(true)} 
                style={{ 
                  background: '#eff6ff', color: '#3b82f6', border: '1.5px solid #dbeafe', 
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', 
                  padding: '0 24px', height: '48px', minWidth: '140px', borderRadius: '14px', 
                  fontWeight: '700', fontSize: '15px', cursor: 'pointer', transition: 'all 0.2s ease', 
                  boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.1)' 
                }}
              >
                <FaUsers /> Company
              </button>
            )}

            {/* The Branches Button */}
            {showBranchAssign && (
              <button 
                type="button"
                className="customize-btn branch-btn" 
                onClick={() => setShowAssignmentModal(true)}
                style={{
                  background: '#fff7ed', color: '#ea580c', border: '1.5px solid #ffedd5',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                  padding: '0 24px', height: '48px', minWidth: '140px', borderRadius: '14px',
                  fontWeight: '700', fontSize: '15px', cursor: 'pointer', transition: 'all 0.2s ease',
                  boxShadow: '0 4px 6px -1px rgba(234, 88, 12, 0.1)'
                }}
              >
                <FaMapMarkerAlt /> Branches
              </button>
            )}

            {/* The Customize Button */}
            <button 
              type="button"
              className="customize-btn" 
              onClick={(e) => { e.preventDefault(); setShowCustomizeModal(true); }}
              style={{
                background: 'rgb(46, 204, 113)', color: '#ffffff', border: 'none',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                padding: '0 24px', height: '48px', minWidth: '140px', borderRadius: '14px',
                fontWeight: '700', fontSize: '15px', cursor: 'pointer', transition: 'all 0.2s ease',
                boxShadow: '0 4px 6px -1px rgba(46, 204, 113, 0.2)'
              }}
            >
              <FaCogs /> Customize
            </button>

            <button className="save-btn" onClick={handleSubmit} disabled={loading}>
              {loading ? "Adding..." : <><FaPlus /> Add Topping</>}
            </button>
          </div>
        </div>
      </div>
      <div className="header-divider"></div>

      {showAssignmentModal && (
        <div className="add-modal-overlay">
          <div className="add-modal" style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <span>Topping Assignments & Options</span>
              <button className="close-modal-btn" onClick={() => setShowAssignmentModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px' }}><FaTimes /></button>
            </div>
            <div className="modal-body" style={{ marginTop: '20px' }}>
              
              {/* Reminder text to show user this modal saves the topping! */}
              {(formData.topping_name || customFieldsData.topping_name) && (
                 <div style={{ backgroundColor: '#e0f2fe', color: '#0369a1', padding: '12px', borderRadius: '8px', marginBottom: '15px', fontSize: '0.95rem', border: '1px solid #bae6fd' }}>
                   You are about to save <strong>{formData.topping_name || customFieldsData.topping_name}</strong>. Please confirm which companies and branches this belongs to.
                 </div>
              )}

              {showCompanyAssign && (
                <div className="assignment-card" style={{ marginBottom: '20px' }}>
                  <h4 style={{ color: '#3b82f6' }}><FaUsers /> Select Company *</h4>
                  <div className="checkbox-group" style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
                    {companyOptions.map((comp, idx) => (
                      <label key={idx} className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 18px', background: '#eff6ff', borderRadius: '12px', border: '1.5px solid #dbeafe', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
                        <input 
                          type="checkbox" 
                          checked={selectedCompanies.includes(comp)} 
                          onChange={(e) => { 
                            if (e.target.checked) setSelectedCompanies(prev => [...prev, comp]);
                            else setSelectedCompanies(prev => prev.filter(c => c !== comp));
                          }} 
                          style={{ width: '18px', height: '18px', accentColor: '#3b82f6' }} 
                        />
                        {comp}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {showBranchAssign && (
                <div className="assignment-card">
                  <h4 style={{ color: '#10b981' }}><FaMapMarkerAlt /> Select Branch</h4>
                  <div className="checkbox-group" style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
                    {availableBranches.map((branchName, idx) => (
                      <label key={idx} className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 18px', background: '#ecfdf5', borderRadius: '12px', border: '1.5px solid #d1fae5', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
                        <input 
                          type="checkbox" 
                          checked={selectedAddBranches.includes(branchName)} 
                          onChange={(e) => { 
                            if (e.target.checked) setSelectedAddBranches(prev => [...prev, branchName]);
                            else setSelectedAddBranches(prev => prev.filter(b => b !== branchName));
                          }} 
                          style={{ width: '18px', height: '18px', accentColor: '#10b981' }} 
                        />
                        {branchName}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="save-btn" onClick={handleConfirmAndSaveAssignments} disabled={loading}>
                {loading ? "Saving..." : ((formData.topping_name || customFieldsData.topping_name) ? "Confirm & Save Topping" : "Confirm Options")}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="customer-scroll-body">
        <div className="form-sections-wrapper" style={{ width: '100%', maxWidth: '1000px', margin: '0 auto' }}>

        {error && (
          <div style={{
            background: 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)', color: '#c0392b', padding: '15px', borderRadius: '10px',
            marginBottom: '20px', textAlign: 'center', border: '1px solid #e74c3c', boxShadow: '0 2px 4px rgba(231, 76, 60, 0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px'
          }}>
            <FaExclamationTriangle style={{ fontSize: '1.2rem' }} />
            {error}
          </div>
        )}
        {success && (
          <div style={{
            background: 'linear-gradient(135deg, #d4edda 0%, #c8e6c9 100%)', color: '#155724', padding: '15px', borderRadius: '10px',
            marginBottom: '20px', textAlign: 'center', border: '1px solid #28a745', boxShadow: '0 2px 4px rgba(40, 167, 69, 0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px'
          }}>
            <FaCheckCircle style={{ fontSize: '1.2rem', color: '#27ae60' }} />
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ marginBottom: '30px' }}>
          <div className="form-section-card theme-blue">
            <div className="section-header">
              <div className="section-icon"><FaCheese /></div>
              <h2>Create New Topping</h2>
            </div>
            <div className="form-grid" style={{ padding: '20px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#2c3e50' }}>Topping Name</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#f8f9fa', padding: '10px 12px', borderRadius: '10px', border: '1px solid #e9ecef' }}>
                    <input
                      type="text"
                      name="topping_name"
                      value={formData.topping_name}
                      onChange={handleChange}
                      placeholder="e.g. Extra Cheese"
                      style={{ flex: 1, border: 'none', background: 'transparent', outline: 'none', color: '#2c3e50' }}
                    />
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#2c3e50' }}>Price</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#f8f9fa', padding: '10px 12px', borderRadius: '10px', border: '1px solid #e9ecef' }}>
                    <input
                      type="number"
                      name="price"
                      value={formData.price}
                      onChange={handleChange}
                      step="0.01"
                      placeholder="0.00"
                      style={{ flex: 1, border: 'none', background: 'transparent', outline: 'none', color: '#2c3e50' }}
                    />
                  </div>
                </div>

                {/* Custom Fields Dynamically Rendered here! */}
                {doctypeData?.fields?.filter(f => !f.hidden && f.id !== 'topping_name' && f.id !== 'price').map(f => (
                  <div key={f.id}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#2c3e50' }}>{f.label}</label>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '8px', background: '#f8f9fa', padding: '10px 12px', borderRadius: '10px', border: '1px solid #e9ecef'
                    }}>
                      <input
                        type="text"
                        value={customFieldsData[f.id] || ""}
                        onChange={(e) => handleCustomFieldChange(f.id, e.target.value)}
                        required={f.mandatory}
                        placeholder={`Enter ${f.label.toLowerCase()}`}
                        style={{ flex: 1, border: 'none', background: 'transparent', outline: 'none', color: '#2c3e50' }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </form>

        <div className="form-section-card theme-green" style={{ marginTop: '20px' }}>
          <div className="section-header">
            <div className="section-icon"><FaSearch /></div>
            <h2>Existing Toppings ({filteredToppings.length})</h2>
          </div>
          <div style={{ padding: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#f8f9fa', padding: '10px 12px', borderRadius: '10px', border: '1px solid #e9ecef', marginBottom: '15px' }}>
              <FaSearch style={{ color: '#7f8c8d' }} />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search toppings..."
                style={{ flex: 1, border: 'none', background: 'transparent', outline: 'none', color: '#2c3e50' }}
              />
            </div>

            <div className="premium-card">
              <table className="premium-table">
                <thead>
                  <tr>
                    <th>Topping Name</th>
                    <th>Price</th>
                    <th>Company</th>
                    {isCompanyAdmin && <th>Branch</th>}
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredToppings.length === 0 ? (
                    <tr>
                      <td colSpan={isCompanyAdmin ? 5 : 4} style={{ textAlign: 'center', padding: '30px', color: '#7f8c8d' }}>
                        No Toppings Cataloged Yet.
                      </td>
                    </tr>
                  ) : (
                    filteredToppings.map((topping) => (
                      <tr key={topping._id}>
                        <td>
                          <div style={{ fontWeight: '600', color: '#2c3e50' }}>
                            {topping.topping_name}
                          </div>
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '4px' }}>
                            {doctypeData?.fields?.filter(f => !f.is_default && !f.hidden && f.id !== 'topping_name' && f.id !== 'price').map(f => (
                              <small key={f.id} style={{ color: '#64748b', fontSize: '0.75rem' }}>
                                | {f.label}: {topping[f.id] || 'N/A'}
                              </small>
                            ))}
                          </div>
                        </td>
                        <td>
                          <span style={{ fontWeight: '700', color: '#2c3e50' }}>{topping.price}</span>
                        </td>
                        <td>
                          <span style={{ 
                            background: '#ecfdf5', color: '#059669', padding: '4px 8px', borderRadius: '6px', fontSize: '12px', fontWeight: '600' 
                          }}>
                            {topping.company_name || 'All'}
                          </span>
                        </td>
                        {isCompanyAdmin && (
                          <td>
                            <span style={{ color: '#64748b', fontSize: '13px' }}>
                              {Array.isArray(topping.branch_name) ? topping.branch_name.join(', ') : (topping.branch_name || 'Global Access')}
                            </span>
                          </td>
                        )}
                        <td className="actions-cell" style={{ textAlign: 'right' }}>
                          <button
                            onClick={() => openEditModal(topping)}
                            disabled={loading}
                            className="action-btn action-btn-edit"
                            title="Edit"
                          >
                            <FaEdit />
                          </button>
                          <button
                            onClick={() => openDeleteModal(topping._id)}
                            disabled={loading}
                            className="action-btn action-btn-delete"
                            title="Delete"
                          >
                            <FaTrash />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        </div>

        {/* The CUSTOMIZE Modal for adding Custom Fields! */}
        <CustomerCustomizationModal
          isOpen={showCustomizeModal}
          onClose={() => setShowCustomizeModal(false)}
          onRefresh={() => fetchDoctype()}
          targetDocType="Toppings"
        />

      </div>

      {showEditModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1100 }}>
          <div style={{ backgroundColor: '#ffffff', padding: '30px', borderRadius: '15px', width: '90%', maxWidth: '400px', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: '#2c3e50', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <FaEdit style={{ color: '#f1c40f' }} /> Edit Topping
              </h3>
              <FaTimes onClick={() => closeEditModal()} style={{ cursor: 'pointer', color: '#7f8c8d' }} />
            </div>
            <form onSubmit={handleUpdate}>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px' }}>Topping Name</label>
                <input
                  type="text"
                  value={editFormData.topping_name}
                  onChange={(e) => setEditFormData({ ...editFormData, topping_name: e.target.value })}
                  style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
                />
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px' }}>Price</label>
                <input
                  type="number"
                  value={editFormData.price}
                  onChange={(e) => setEditFormData({ ...editFormData, price: e.target.value })}
                  step="0.01"
                  style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
                />
              </div>
              
              {/* Dynamic Edit Fields */}
              {doctypeData?.fields?.filter(f => !f.hidden && f.id !== 'topping_name' && f.id !== 'price').map(f => (
                <div key={f.id} style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px' }}>{f.label}</label>
                  <input
                    type="text"
                    value={editCustomFieldsData[f.id] || ""}
                    onChange={(e) => handleCustomFieldChange(f.id, e.target.value, true)}
                    required={f.mandatory}
                    style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
                  />
                </div>
              ))}

              {showEditCompanyAssign && (
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#7f8c8d' }}>Company</label>
                  <div style={{ backgroundColor: '#f8f9fa', padding: '10px', borderRadius: '10px', border: '1px solid #e9ecef', color: '#2c3e50', fontWeight: '500' }}>
                    {toppingToEdit?.company_name || 'N/A'}
                  </div>
                </div>
              )}
              {showEditBranchAssign && (
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#7f8c8d' }}>Select Branches</label>
                  <div style={{ maxHeight: '120px', overflowY: 'auto', border: '1px solid #e9ecef', borderRadius: '10px', padding: '10px', backgroundColor: '#f8f9fa' }}>
                    {uniqueEditAvailableBranches.length === 0 ? <p style={{ fontSize: '0.85rem', color: '#7f8c8d' }}>Loading branches...</p> : 
                      uniqueEditAvailableBranches.map((branchName, idx) => (
                        <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                          <input
                            type="checkbox"
                            id={`edit-branch-${idx}`}
                            checked={editBranches.includes(branchName)}
                            onChange={() => {
                              setEditBranches(prev => prev.includes(branchName) ? prev.filter(b => b !== branchName) : [...prev, branchName]);
                            }}
                          />
                          <label htmlFor={`edit-branch-${idx}`} style={{ fontSize: '0.9rem', color: '#2c3e50', cursor: 'pointer' }}>{branchName}</label>
                        </div>
                      ))}
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', gap: '15px' }}>
                <button type="submit" disabled={loading} style={{ flex: 1, padding: '12px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600' }}>
                  {loading ? 'Updating...' : 'Save'}
                </button>
                <button type="button" onClick={() => closeEditModal()} style={{ flex: 1, padding: '12px', background: '#7f8c8d', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600' }}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }} onClick={() => setShowDeleteModal(false)}>
          <div style={{ backgroundColor: '#ffffff', padding: '30px', borderRadius: '15px', width: '90%', maxWidth: '400px', textAlign: 'center', border: '1px solid #e9ecef' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ color: '#e74c3c', marginBottom: '15px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}><FaTrash /> Confirm Delete</h3>
            <p style={{ color: '#2c3e50', marginBottom: '25px' }}>Are you sure you want to delete this topping?</p>
            <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
              <button onClick={() => handleDelete(toppingToDelete)} disabled={loading} style={{ padding: '10px 20px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '25px', cursor: 'pointer', fontWeight: '600' }}>
                {loading ? 'Deleting...' : 'Yes, Delete'}
              </button>
              <button onClick={() => setShowDeleteModal(false)} style={{ padding: '10px 20px', background: '#3498db', color: 'white', border: 'none', borderRadius: '25px', cursor: 'pointer', fontWeight: '600' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
